import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { authAPI } from '../lib/api';

/**
 * UI language switcher.
 *
 *   - Persists immediately via i18next's localStorage detector (cache: 'localStorage').
 *   - Best-effort syncs the choice to the backend (PATCH /api/auth/me) so it
 *     follows the user across devices. A network failure here is non-blocking —
 *     the local switch always succeeds.
 *
 * Flags are inline SVGs (not emoji) so they render reliably on Windows / Linux /
 * older browsers that don't ship a color emoji font.
 */
const FlagFR = (props) => (
    <svg viewBox="0 0 3 2" width="18" height="12" aria-hidden="true" {...props}>
        <rect width="1" height="2" x="0" fill="#0055A4" />
        <rect width="1" height="2" x="1" fill="#FFFFFF" />
        <rect width="1" height="2" x="2" fill="#EF4135" />
    </svg>
);

const FlagGB = (props) => (
    <svg viewBox="0 0 60 30" width="18" height="12" aria-hidden="true" {...props}>
        <clipPath id="lang-gb-clip"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
        <clipPath id="lang-gb-clip2">
            <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <g clipPath="url(#lang-gb-clip)">
            <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
            <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
            <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#lang-gb-clip2)" stroke="#C8102E" strokeWidth="4" />
            <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
            <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
        </g>
    </svg>
);

const LANGS = [
    { code: 'fr', label: 'Français', Flag: FlagFR },
    { code: 'en', label: 'English', Flag: FlagGB },
];

export const LanguageSwitcher = ({ compact = false }) => {
    const { i18n } = useTranslation();
    const current = (i18n.resolvedLanguage || i18n.language || 'fr').slice(0, 2);
    const active = LANGS.find((l) => l.code === current) || LANGS[0];
    const ActiveFlag = active.Flag;

    const change = async (code) => {
        if (code === current) return;
        await i18n.changeLanguage(code);
        document.documentElement.lang = code;
        try { localStorage.setItem('uc.lang', code); } catch { /* private mode */ }
        // Persist to backend so the choice follows the user across devices.
        // Fail-soft: a network hiccup must not block the local switch.
        try {
            await authAPI.updatePreferredLanguage(code);
        } catch (err) {
            console.warn('[i18n] failed to sync preferred_language to backend', err?.response?.status, err?.message);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                data-testid={compact ? 'language-switcher-mobile' : 'language-switcher-desktop'}
                className="flex items-center gap-2 h-9 px-3 rounded-md border border-[#E5E5E5] hover:border-[#C08478] hover:bg-[#FAFAFA] transition text-sm"
                aria-label="Change language"
            >
                <Globe size={14} className="text-[#C08478]" />
                {!compact && <span className="font-medium uppercase">{active.code}</span>}
                <ActiveFlag className="shrink-0 rounded-sm border border-black/5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
                {LANGS.map((l) => {
                    const F = l.Flag;
                    return (
                        <DropdownMenuItem
                            key={l.code}
                            onClick={() => change(l.code)}
                            data-testid={`lang-option-${l.code}`}
                            className={`flex items-center gap-2 cursor-pointer ${
                                l.code === current ? 'font-semibold text-[#1A1A1A]' : ''
                            }`}
                        >
                            <F className="shrink-0 rounded-sm border border-black/5" />
                            <span>{l.label}</span>
                            {l.code === current && <span className="ml-auto text-[#C08478]">●</span>}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default LanguageSwitcher;
